// Background capture pipeline: the shutter only grabs a frame; JPEG encoding,
// EXIF injection and the chunked upload run here, off the shutter path.
import { injectExif } from "./exif";
import { uploadChunked } from "./uploader";
import { enqueueCapture } from "./offlineQueue";

export const JPEG_QUALITY = 0.88;
const CONCURRENCY = 2;
const MAX_ATTEMPTS = 3;

const jobs = [];
let running = 0;
let pending = 0;
const listeners = new Set();

const emit = () => listeners.forEach((cb) => cb(pending));
export const onPendingChange = (cb) => {
  listeners.add(cb);
  cb(pending);
  return () => listeners.delete(cb);
};
export const pendingUploads = () => pending;

const canvasToDataUrl = (canvas) =>
  new Promise((resolve, reject) => {
    if (!canvas.toBlob) return resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("encode failed"));
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    }, "image/jpeg", JPEG_QUALITY);
  });

const isNetworkError = (err) => {
  const m = (err?.message || "").toLowerCase();
  return !navigator.onLine || m.includes("failed to fetch") || m.includes("network") || m.includes("load failed");
};

async function runJob(job) {
  const dataUrl = injectExif(await canvasToDataUrl(job.canvas), job.exif);
  job.canvas.width = 0; // release the frame buffer early
  job.onEncoded?.(dataUrl);
  const payload = {
    filename: job.filename, sequence: job.sequence, tags: job.tags, image_data: dataUrl,
    width: job.width, height: job.height, size_bytes: Math.round((dataUrl.length * 3) / 4),
  };
  for (let attempt = 1; ; attempt++) {
    try {
      const done = await uploadChunked({
        sessionId: job.sessionId, filename: job.filename, sequence: job.sequence, tags: job.tags,
        dataUrl, metadata: job.metadata,
      });
      job.onUploaded?.(done);
      return;
    } catch (err) {
      if (isNetworkError(err) || attempt >= MAX_ATTEMPTS) {
        // Never lose a shot: park it in the offline queue (drained from the Sessions/Sync screens).
        await enqueueCapture(job.sessionId, payload);
        job.onQueuedOffline?.(isNetworkError(err));
        return;
      }
      await new Promise((r) => setTimeout(r, 800 * attempt));
    }
  }
}

function pump() {
  while (running < CONCURRENCY && jobs.length) {
    const job = jobs.shift();
    running += 1;
    runJob(job)
      .catch((e) => job.onError?.(e))
      .finally(() => {
        running -= 1;
        pending -= 1;
        emit();
        pump();
      });
  }
}

export function queueShot(job) {
  jobs.push(job);
  pending += 1;
  emit();
  pump();
}
